import React, { useEffect, useState } from 'react';
import api from './api';

function getTransactionDetails(tx) {
  const metadata = tx?.metadata || {};
  const opponentLabel = metadata.opponentUsername || metadata.opponentName || '';
  const sessionLabel = metadata.targetWinsLabel || '';
  const baseMatchLabel = [opponentLabel ? `vs ${opponentLabel}` : '', sessionLabel].filter(Boolean).join(' · ');

  if (tx?.type === 'agent-credit') return metadata.agentName ? `From ${metadata.agentName}` : '';
  if (tx?.type === 'agent-debit') return metadata.agentName ? `To ${metadata.agentName}` : '';
  if (tx?.type === 'agent-commission') return metadata.playerUsername || metadata.playerName ? `Earned from ${metadata.playerUsername || metadata.playerName}` : '';
  if (tx?.type === 'stake-lock') return [baseMatchLabel, metadata.stake ? `Stake ${metadata.stake}` : ''].filter(Boolean).join(' · ');
  if (tx?.type === 'platform-fee') return [baseMatchLabel, metadata.fee ? `Platform fee ${metadata.fee}` : ''].filter(Boolean).join(' · ');
  if (tx?.type === 'match-win') return [baseMatchLabel, metadata.payout ? `Won ${metadata.payout}` : ''].filter(Boolean).join(' · ');
  if (tx?.type === 'refund') return [baseMatchLabel, metadata.reason ? String(metadata.reason).replace(/-/g, ' ') : 'Refund'].filter(Boolean).join(' · ');
  if (tx?.type === 'superadmin-credit') return metadata.superadminName || metadata.superadminEmail || '';
  if (tx?.type === 'superadmin-debit') return metadata.superadminName || metadata.superadminEmail || '';
  return '';
}

function humanizeTxType(type) {
  return String(type || '')
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

export default function PlayerTransactionsPage({ user, onBack }) {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ page: 1, totalPages: 1, total: 0 });
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const { body, documentElement } = document;
    const root = document.getElementById('root');
    const prev = {
      htmlOverflow: documentElement.style.overflow,
      htmlHeight: documentElement.style.height,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyHeight: body.style.height,
      rootOverflow: root?.style.overflow || '',
      rootHeight: root?.style.height || '',
    };

    documentElement.style.overflow = 'auto';
    documentElement.style.height = 'auto';
    body.style.overflow = 'auto';
    body.style.position = 'static';
    body.style.height = 'auto';
    if (root) {
      root.style.overflow = 'visible';
      root.style.height = 'auto';
    }

    return () => {
      documentElement.style.overflow = prev.htmlOverflow;
      documentElement.style.height = prev.htmlHeight;
      body.style.overflow = prev.bodyOverflow;
      body.style.position = prev.bodyPosition;
      body.style.height = prev.bodyHeight;
      if (root) {
        root.style.overflow = prev.rootOverflow;
        root.style.height = prev.rootHeight;
      }
    };
  }, []);

  useEffect(() => {
    loadPage(1);
  }, []);

  async function loadPage(page) {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/wallet/activities?page=${page}&limit=10`);
      const next = res.data?.data || {};
      setRows(next.items || []);
      setMeta({
        page: Number(next.page || 1),
        totalPages: Number(next.totalPages || 1),
        total: Number(next.total || 0),
      });
    } catch (_err) {
      setError('Failed to load transactions');
      setRows([]);
      setMeta({ page: 1, totalPages: 1, total: 0 });
    } finally {
      setLoading(false);
    }
  }

  const visibleRows = rows.filter((row) => {
    if (filter === 'deposit') return Number(row.amount || 0) > 0;
    if (filter === 'withdrawal') return Number(row.amount || 0) < 0;
    return true;
  });

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.headerCard}>
          <div>
            <div style={styles.eyebrow}>Wallet History</div>
            <h1 style={styles.h1}>Transactions</h1>
            <div style={styles.sub}>{user?.name}{user?.email ? ` · ${user.email}` : ''}</div>
          </div>
          <button type="button" onClick={onBack} style={styles.backBtn}>Back</button>
        </div>

        <div style={styles.tableCard}>
          <div style={styles.tableHeader}>
            <div>
              <div style={styles.tableTitle}>Coin Transactions</div>
              <div style={styles.tableSub}>Track where your coins came from and where they went.</div>
            </div>
            <div style={styles.countPill}>{meta.total} rows</div>
          </div>

          <div style={styles.filterRow}>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} style={styles.filterSelect}>
              <option value="all">All</option>
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
            </select>
          </div>

          {loading && <div style={styles.empty}>Loading...</div>}
          {error && <div style={{ ...styles.empty, color: '#ffb0b0' }}>{error}</div>}
          {!loading && !error && visibleRows.length === 0 && <div style={styles.empty}>No transactions yet.</div>}

          {!loading && !error && visibleRows.length > 0 && (
            <>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Details</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Amount</th>
                      <th style={{ ...styles.th, textAlign: 'right' }}>Balance</th>
                      <th style={styles.th}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row) => (
                      <tr key={row._id}>
                        <td style={styles.td}><div style={styles.primaryText}>{humanizeTxType(row.type)}</div></td>
                        <td style={styles.td}>
                          <div style={styles.primaryText}>{getTransactionDetails(row) || 'No extra details'}</div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <div style={{ ...styles.primaryText, color: row.amount >= 0 ? '#c58f55' : '#d17f72' }}>
                            {row.amount >= 0 ? '+' : ''}{Number(row.amount || 0).toLocaleString()}
                          </div>
                        </td>
                        <td style={{ ...styles.td, textAlign: 'right' }}>
                          <div style={styles.primaryText}>{Number(row.balanceAfter || 0).toLocaleString()}</div>
                        </td>
                        <td style={styles.td}><div style={styles.secondaryText}>{new Date(row.createdAt).toLocaleString()}</div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div style={styles.paginationRow}>
                <button
                  type="button"
                  onClick={() => loadPage(Math.max(1, meta.page - 1))}
                  disabled={loading || meta.page <= 1}
                  style={{ ...styles.pageBtn, ...(loading || meta.page <= 1 ? styles.pageBtnDisabled : {}) }}
                >
                  Previous
                </button>
                <div style={styles.pageLabel}>Page {meta.page} of {meta.totalPages}</div>
                <button
                  type="button"
                  onClick={() => loadPage(Math.min(meta.totalPages, meta.page + 1))}
                  disabled={loading || meta.page >= meta.totalPages}
                  style={{ ...styles.pageBtn, ...(loading || meta.page >= meta.totalPages ? styles.pageBtnDisabled : {}) }}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: 'radial-gradient(circle at top, #3b1f10 0%, #160b06 72%)',
    padding: '18px 18px 28px',
    overflowY: 'auto',
  },
  container: {
    maxWidth: 1180,
    margin: '0 auto',
    display: 'grid',
    gap: 14,
    paddingBottom: 32,
  },
  headerCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    padding: 16,
    borderRadius: 20,
    background: 'rgba(36,19,10,0.95)',
    border: '1px solid rgba(215,177,106,0.22)',
    boxShadow: '0 16px 36px rgba(0,0,0,0.2)',
    flexWrap: 'wrap',
  },
  eyebrow: {
    color: '#d8c29a',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 1.3,
    fontWeight: 800,
  },
  h1: {
    margin: '4px 0 0',
    color: '#f6e7c1',
    fontSize: 30,
  },
  sub: {
    color: '#d8c29a',
    marginTop: 6,
    fontSize: 14,
    wordBreak: 'break-word',
  },
  backBtn: {
    border: '1px solid #d7b16a',
    background: '#7a4c22',
    color: '#f6e7c1',
    borderRadius: 10,
    padding: '10px 14px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  tableCard: {
    background: 'rgba(36,19,10,0.95)',
    border: '1px solid rgba(215,177,106,0.28)',
    borderRadius: 20,
    padding: 16,
    display: 'grid',
    gap: 14,
    boxShadow: '0 18px 38px rgba(0,0,0,0.18)',
  },
  tableHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 12,
    flexWrap: 'wrap',
  },
  tableTitle: {
    color: '#ffd48a',
    fontSize: 20,
    fontWeight: 800,
  },
  tableSub: {
    color: '#d8c29a',
    fontSize: 13,
    marginTop: 4,
  },
  countPill: {
    borderRadius: 999,
    padding: '8px 12px',
    background: 'rgba(215,177,106,0.12)',
    border: '1px solid rgba(215,177,106,0.2)',
    color: '#f6e7c1',
    fontWeight: 700,
    fontSize: 13,
  },
  filterRow: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  filterSelect: {
    width: 180,
    borderRadius: 12,
    border: '1px solid rgba(215,177,106,0.25)',
    background: 'rgba(58,35,20,0.96)',
    color: '#f6e7c1',
    padding: '10px 12px',
    outline: 'none',
  },
  tableWrap: {
    overflowX: 'auto',
    border: '1px solid rgba(215,177,106,0.16)',
    borderRadius: 16,
    background: 'rgba(29,15,8,0.9)',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 860,
  },
  th: {
    textAlign: 'left',
    padding: '12px 14px',
    color: '#ffd48a',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    borderBottom: '1px solid rgba(215,177,106,0.15)',
    background: 'rgba(36,19,10,0.98)',
  },
  td: {
    padding: '11px 14px',
    borderBottom: '1px solid rgba(215,177,106,0.1)',
    color: '#f6e7c1',
    verticalAlign: 'top',
  },
  primaryText: {
    color: '#f6e7c1',
    fontSize: 14,
    lineHeight: 1.25,
  },
  secondaryText: {
    color: '#d8c29a',
    fontSize: 12,
    lineHeight: 1.3,
  },
  paginationRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 10,
    flexWrap: 'wrap',
  },
  pageBtn: {
    borderRadius: 12,
    border: '1px solid rgba(215,177,106,0.25)',
    background: 'rgba(58,35,20,0.96)',
    color: '#f6e7c1',
    padding: '9px 12px',
    cursor: 'pointer',
    fontWeight: 700,
  },
  pageBtnDisabled: {
    opacity: 0.45,
    cursor: 'not-allowed',
  },
  pageLabel: {
    color: '#d8c29a',
    fontSize: 13,
  },
  empty: {
    padding: 18,
    color: '#d8c29a',
  },
};
